import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useDisplayLearningActivityCardImage } from "lib/hocs/useLearningActivityCardState";
import SettingStore from "stores/settings";
import PublicAdminSettingStore from "stores/publicAdminSettings";
import {
  STORAGE_KEY_CATALOGUE_VIEW,
  CATALOGUE_PAGE_SIZE,
  QUERY_KEYS,
  CARD_VIEW,
  SETTING_KEY_INCLUDE_NEXT_ACTIVITY_IN_OVERVIEW,
  SETTING_KEY_RATING_POPUP_SERVER,
} from "../constants";

/** Loads rating-popup and include-next-activity flags from stores; updates UI state only while mounted. */
async function loadRatingAndNextActivitySettingsFromStores({
  setUIState,
  getIsMounted,
}) {
  let nextDisplayRating = false;
  let nextIncludeNextActivityInOverview = false;
  try {
    await PublicAdminSettingStore.init();
    try {
      await SettingStore.init();
    } catch {
      // Authenticated endpoint; anonymous / expired sessions stay on public store + fallbacks below
    }

    const fromPublicRating = PublicAdminSettingStore.getSettingValue(
      SETTING_KEY_RATING_POPUP_SERVER,
    );
    const ratingPopupRaw =
      fromPublicRating != null
        ? String(fromPublicRating)
        : SettingStore.getSettingValue(SETTING_KEY_RATING_POPUP_SERVER);
    nextDisplayRating = String(ratingPopupRaw) === "1";

    const fromPublicIncludeNext = PublicAdminSettingStore.getSettingValue(
      SETTING_KEY_INCLUDE_NEXT_ACTIVITY_IN_OVERVIEW,
    );
    const includeNextActivityRaw =
      fromPublicIncludeNext != null
        ? String(fromPublicIncludeNext)
        : SettingStore.getSettingValue(
            SETTING_KEY_INCLUDE_NEXT_ACTIVITY_IN_OVERVIEW,
          );
    nextIncludeNextActivityInOverview = String(includeNextActivityRaw) === "1";
  } catch {
    // Defaults above; still mark loaded so toolbar can settle
  } finally {
    if (getIsMounted()) {
      setUIState((prev) => ({
        ...prev,
        displayRating: nextDisplayRating,
        includeNextActivityInOverview: nextIncludeNextActivityInOverview,
        includeNextActivitySettingLoaded: true,
      }));
    }
  }
}

const UIContext = createContext();

/**
 * Custom hook to use the UIContext
 * @returns {Object} The UI context value
 * @throws {Error} When used outside of a UIProvider
 */
export const useUIContext = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useUIContext must be used within a UIProvider");
  }
  return context;
};

/**
 * UIProvider component that manages UI state and interactions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} UIProvider component
 */
export const UIProvider = ({ children, public: isPublic = false }) => {
  const displayImageSetting = useDisplayLearningActivityCardImage();

  // Check URL params first, then fallback to localStorage
  const urlParams = new URLSearchParams(
    (window.location.hash || "").split("?")[1] || "",
  );
  const urlView = urlParams.get(QUERY_KEYS.view);
  const storedLearningView =
    localStorage.getItem(STORAGE_KEY_CATALOGUE_VIEW) === "true";

  // When public is true, always show images regardless of user settings
  const effectiveDisplayImage = isPublic ? true : displayImageSetting;

  // Calculate default list view: URL params first, then localStorage, then display image setting
  let defaultListView;
  if (urlView !== null) {
    // URL param has precedence
    defaultListView = urlView.toLowerCase() !== CARD_VIEW;
  } else if (storedLearningView !== null && storedLearningView !== undefined) {
    // Fallback to localStorage
    defaultListView = storedLearningView;
  } else {
    // Final fallback to display image setting
    defaultListView = !effectiveDisplayImage;
  }

  const [uiState, setUIState] = useState({
    listView: defaultListView,
    displayImage: displayImageSetting ?? false, // Default to false while loading
    displayRating: false,
    /** Mirrors `includenextactivityinoverview === "1"` once loaded; starts `false` (same as setting `"0"`) so the next-application sort is hidden until `includeNextActivitySettingLoaded`. */
    includeNextActivityInOverview: false,
    /** Set `true` after public/authenticated admin settings have been read (or attempted). */
    includeNextActivitySettingLoaded: false,
    showAdvancedFilter: false,
    detailsView: false,
    page: 1,
    pageSize: CATALOGUE_PAGE_SIZE,
    selectedActivity: null,
  });

  // Update UI state when displayImageSetting changes (after async fetch)
  useEffect(() => {
    setUIState((prev) => ({
      ...prev,
      displayImage: effectiveDisplayImage,
      // Only update listView if no stored preference exists
      listView:
        storedLearningView !== null && storedLearningView !== undefined
          ? storedLearningView
          : !effectiveDisplayImage,
    }));
  }, [effectiveDisplayImage, storedLearningView]);

  // Initialize displayRating from SettingStore; include-next-activity from public admin settings API
  useEffect(() => {
    let isMounted = true;

    void loadRatingAndNextActivitySettingsFromStores({
      setUIState,
      getIsMounted: () => isMounted,
    });

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Updates the UI state with new values
   * @param {Object} updates - Object containing updates to apply to UI state
   * @param {boolean} skipLocalStorage - If true, skip saving to localStorage (e.g., when applying URL params)
   */
  const updateUIState = useCallback((updates, skipLocalStorage = false) => {
    setUIState((prev) => {
      const next = { ...prev, ...updates };
      // Only save listView to localStorage if not skipping and listView is being updated
      if (
        skipLocalStorage ||
        !Object.prototype.hasOwnProperty.call(updates, "listView")
      ) {
        return next;
      }
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(
            STORAGE_KEY_CATALOGUE_VIEW,
            String(next.listView),
          );
        }
      } catch {
        // Ignore localStorage errors
      }
      return next;
    });
  }, []);

  /**
   * Toggles between list and grid view
   */
  const toggleListView = useCallback(() => {
    setUIState((prev) => {
      const newListView = !prev.listView;
      localStorage.setItem(STORAGE_KEY_CATALOGUE_VIEW, newListView);
      return { ...prev, listView: newListView };
    });
  }, []);

  /**
   * Toggles the details view for a specific activity
   * @param {Object|null} selectedActivity - The activity to show details for, or null to hide details
   */
  const toggleDetailsView = useCallback((selectedActivity = null) => {
    setUIState((prev) => ({
      ...prev,
      detailsView: !prev.detailsView,
      selectedActivity,
    }));
  }, []);

  /**
   * Toggles the advanced filter visibility
   */
  const handleShowAdvancedFilter = useCallback(() => {
    setUIState((prev) => ({
      ...prev,
      showAdvancedFilter: !prev.showAdvancedFilter,
    }));
  }, []);

  /**
   * Sets the current page number
   * @param {number} page - The page number to set
   */
  const setPage = useCallback((page) => {
    setUIState((prev) => ({ ...prev, page }));
  }, []);

  /**
   * Gets courses for the current page based on pagination
   * @param {Array} allCourses - All available courses
   * @param {number} page - Current page number
   * @param {number} pageSize - Number of items per page
   * @returns {Array} Courses for the current page
   */
  const getCoursesForCurrentPage = useCallback((allCourses, page, pageSize) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allCourses.slice(startIndex, endIndex);
  }, []);

  const value = {
    uiState,
    updateUIState,
    toggleListView,
    toggleDetailsView,
    handleShowAdvancedFilter,
    setPage,
    getCoursesForCurrentPage,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

UIProvider.propTypes = {
  children: PropTypes.node.isRequired,
  public: PropTypes.bool,
};
